import {
  GIVE_UP_MS,
  IDLE,
  LINK_MAX_AGE_MS,
  MAX_PREPARING,
  fromServer,
  pendingPreparations,
  pollDelayMs,
  preparingCount,
} from "./logic";

// Each recording's download in Download everything, without React: what the
// row shows (idle → checking → ready | preparing → ready | archived | failed),
// archived recordings never ask for a download (they can only be deleted),
// the checks while an MP4 is prepared, and which preparations to resume after
// the screen was left. useLibrary renders it; tests drive it with fake timers.
//
// deps:
//   requestRecording(slug, { check })  evt.live (components/library/api.js)
//   open(url)                          start the file download
//   onReady(rec)                       an MP4 prepared while the user waited
//   onBusy()                           MAX_PREPARING are being prepared already
//   load() / save(pending)             the device's { slug: startedAt } list
//   onChange(states)                   every state change
//   track(event, props)                analytics
//   now(), setTimeout, clearTimeout
export function createDownloadController({
  requestRecording,
  open,
  onReady = () => {},
  onBusy = () => {},
  load = async () => ({}),
  save = () => {},
  onChange = () => {},
  track = () => {},
  now = Date.now,
  setTimer = (fn, ms) => setTimeout(fn, ms),
  clearTimer = (id) => clearTimeout(id),
}) {
  let states = {};
  let pending = {};
  const timers = {};
  let disposed = false;

  const set = (slug, next) => {
    if (disposed) return;
    states = { ...states, [slug]: next };
    onChange(states);
  };

  const remember = (slug, startedAt) => {
    const next = { ...pending };
    if (startedAt) next[slug] = startedAt;
    else delete next[slug];
    pending = next;
    save(next);
  };

  function poll(rec, attempt, retryAfterS, startedAt) {
    clearTimer(timers[rec.slug]);
    timers[rec.slug] = setTimer(async () => {
      delete timers[rec.slug];
      if (disposed) return;
      if (now() - startedAt > GIVE_UP_MS) {
        remember(rec.slug, null);
        set(rec.slug, { status: "failed", error: null, gaveUp: true });
        return;
      }
      let next;
      try {
        next = fromServer(await requestRecording(rec.slug, { check: true }));
      } catch (error) {
        // A failed check (offline, a slow answer) isn't a failed build:
        // keep showing Preparing… and ask again later.
        if (!disposed) poll(rec, attempt + 1, null, startedAt);
        return;
      }
      if (disposed) return;
      if (next.status === "preparing") {
        set(rec.slug, { ...next, startedAt });
        poll(rec, attempt + 1, next.retryAfterS, startedAt);
        return;
      }
      remember(rec.slug, null);
      set(rec.slug, { ...next, at: now(), prepared: true });
      if (next.status === "ready") {
        track("Library Recording Prepared", {
          minutes: Math.round((now() - startedAt) / 60000),
        });
        onReady(rec);
      }
    }, pollDelayMs(attempt, retryAfterS));
  }

  // Download (or Try again) on a row. Archived recordings are never
  // downloaded: their row only offers Delete.
  async function download(rec) {
    if (rec.archived) return;
    const current = states[rec.slug] || IDLE;
    if (current.status === "checking" || current.status === "preparing")
      return;
    // A link we already have opens straight away, inside the click (so the
    // browser never blocks it).
    if (
      current.status === "ready" &&
      current.at &&
      now() - current.at < LINK_MAX_AGE_MS
    ) {
      track("Library Recording Downloaded", { prepared: !!current.prepared });
      open(current.url);
      return;
    }
    if (preparingCount(states) >= MAX_PREPARING) {
      onBusy();
      return;
    }
    set(rec.slug, { status: "checking" });
    let next;
    try {
      next = fromServer(await requestRecording(rec.slug));
    } catch (error) {
      set(rec.slug, { status: "failed", error: error?.message || null });
      return;
    }
    if (disposed) return;
    if (next.status === "preparing") {
      const startedAt = now();
      remember(rec.slug, startedAt);
      set(rec.slug, { ...next, startedAt });
      track("Library Recording Preparing");
      poll(rec, 0, next.retryAfterS, startedAt);
      return;
    }
    set(rec.slug, { ...next, at: now() });
    if (next.status === "ready") {
      track("Library Recording Downloaded", { prepared: false });
      open(next.url);
    }
  }

  // Keep checking the recordings that were being prepared when the screen was
  // last left (only those still in the list, and not older than GIVE_UP_MS).
  async function resume(recordings) {
    const saved = pendingPreparations(await load(), now());
    if (disposed) return;
    pending = { ...saved, ...pending };
    for (const rec of recordings) {
      if (rec.archived) continue;
      const startedAt = saved[rec.slug];
      const current = states[rec.slug];
      if (!startedAt || (current && current.status !== "idle")) continue;
      set(rec.slug, { status: "preparing", startedAt });
      poll(rec, 0, 5, startedAt);
    }
  }

  function dispose() {
    disposed = true;
    Object.values(timers).forEach(clearTimer);
  }

  return { download, resume, dispose, states: () => states };
}
